import type {
  FocusNotification,
  NotificationSubCategory,
} from "@/backend/focus";
import {
  NotificationCategory,
  NotificationStatus,
  NotificationSubCategory as NotificationSubCategoryValue,
} from "@/backend/focus";
import {
  FEED_MANAGER_ICON_PICKER_KEYS,
  type FeedSurfaceIconKey,
  getFeedSurfaceIconKey,
} from "@/utils/feed-surface";

export type NotificationViewBuiltinId =
  | "all"
  | "whales"
  | "money"
  | "social"
  | "messages";

export type NotificationViewRef =
  | { type: "builtin"; id: NotificationViewBuiltinId }
  | { type: "custom-notification-view"; id: string };

export type SerializableNotificationFilter = {
  name: string;
  type: string;
  value: unknown;
};

export type SerializableNotificationSort = {
  direction: "asc" | "desc";
  name: string;
  timePeriod?: unknown;
  type: string;
};

export type NotificationViewHiddenTypesMode = "custom" | "global";

export type FocusCustomNotificationView = {
  createdAt: string;
  filters: SerializableNotificationFilter[];
  hiddenNotificationTypes: NotificationSubCategory[];
  hiddenNotificationTypesMode: NotificationViewHiddenTypesMode;
  iconKey?: FeedSurfaceIconKey;
  id: string;
  name: string;
  sort: SerializableNotificationSort | null;
  updatedAt: string;
};

export type NotificationViewResolution = {
  customView?: FocusCustomNotificationView;
  filters: SerializableNotificationFilter[];
  hiddenNotificationTypes: NotificationSubCategory[];
  hiddenNotificationTypesMode: NotificationViewHiddenTypesMode;
  iconKey: FeedSurfaceIconKey;
  key: string;
  label: string;
  ref: NotificationViewRef;
  sort: SerializableNotificationSort | null;
  type: NotificationViewRef["type"];
};

export type NotificationViewPreferences = {
  customNotificationViews: Record<string, FocusCustomNotificationView>;
  notificationViewOrder: NotificationViewRef[];
};

export const NOTIFICATION_VIEW_FIELDS = {
  amountUsdCents: "amountUsdCents",
  wealthUsdCents: "wealthUsdCents",
  afterDaysAgo: "afterDaysAgo",
  post: "post",
  user: "user",
  status: "status",
  subcategory: "subcategory",
  category: "category",
  categories: "categories",
  subcategories: "subcategories",
  excludedCategories: "excludedCategories",
  excludedSubcategories: "excludedSubcategories",
} as const;

const WHALE_THRESHOLD_USD = 1000;

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return !!value && typeof value === "object" && !Array.isArray(value);
};

const NOTIFICATION_SUBCATEGORY_VALUES = new Set<NotificationSubCategory>(
  Object.values(NotificationSubCategoryValue),
);

export const normalizeHiddenNotificationTypes = (
  value: unknown,
): NotificationSubCategory[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<NotificationSubCategory[]>((types, entry) => {
    if (
      typeof entry !== "string" ||
      !NOTIFICATION_SUBCATEGORY_VALUES.has(entry as NotificationSubCategory)
    ) {
      return types;
    }

    const notificationType = entry as NotificationSubCategory;

    if (!types.includes(notificationType)) {
      types.push(notificationType);
    }

    return types;
  }, []);
};

export const normalizeNotificationViewHiddenTypesMode = (
  value: unknown,
): NotificationViewHiddenTypesMode => {
  return value === "custom" ? "custom" : "global";
};

const isSerializableFilterValue = (value: unknown): boolean => {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.every((entry) => typeof entry === "string");
  }

  if (!isRecord(value)) {
    return false;
  }

  return Object.values(value).every(
    (entry) =>
      entry === undefined ||
      entry === null ||
      typeof entry === "string" ||
      typeof entry === "number" ||
      typeof entry === "boolean",
  );
};

const normalizeNotificationFilter = (
  value: unknown,
): SerializableNotificationFilter | null => {
  if (!isRecord(value)) {
    return null;
  }

  if (typeof value.name !== "string" || typeof value.type !== "string") {
    return null;
  }

  if (!isSerializableFilterValue(value.value)) {
    return null;
  }

  return {
    name: value.name,
    type: value.type,
    value: value.value,
  };
};

export const normalizeNotificationFilters = (
  value: unknown,
): SerializableNotificationFilter[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((entry) => {
    const filter = normalizeNotificationFilter(entry);
    return filter ? [filter] : [];
  });
};

export const normalizeNotificationSort = (
  value: unknown,
): SerializableNotificationSort | null => {
  if (!isRecord(value)) {
    return null;
  }

  if (
    typeof value.name !== "string" ||
    typeof value.type !== "string" ||
    (value.direction !== "asc" && value.direction !== "desc")
  ) {
    return null;
  }

  return {
    direction: value.direction,
    name: value.name,
    timePeriod: isSerializableFilterValue(value.timePeriod)
      ? value.timePeriod
      : undefined,
    type: value.type,
  };
};

export const BUILTIN_NOTIFICATION_VIEW_REGISTRY = {
  all: {
    id: "all",
    label: "All",
    description: "Every unread notification.",
    iconKey: "bell",
    filters: [],
    sort: null,
    canMove: false,
    canRemoveFromDisplay: false,
    defaultVisible: true,
  },
  whales: {
    id: "whales",
    label: "Whales",
    description: "Notifications from accounts with at least $1k wealth.",
    iconKey: "whale",
    filters: [
      {
        type: "numeric",
        name: NOTIFICATION_VIEW_FIELDS.wealthUsdCents,
        value: {
          min: WHALE_THRESHOLD_USD,
        },
      },
    ],
    sort: null,
    canMove: true,
    canRemoveFromDisplay: true,
    defaultVisible: true,
  },
  money: {
    id: "money",
    label: "Money",
    description: "Money and transaction notifications.",
    iconKey: "hand-coins",
    filters: [
      {
        type: "select",
        name: NOTIFICATION_VIEW_FIELDS.category,
        value: NotificationCategory.MONEY,
      },
    ],
    sort: null,
    canMove: true,
    canRemoveFromDisplay: true,
    defaultVisible: true,
  },
  social: {
    id: "social",
    label: "Social",
    description: "Follows, mentions, reactions, and social post activity.",
    iconKey: "users",
    filters: [
      {
        type: "select",
        name: NOTIFICATION_VIEW_FIELDS.category,
        value: NotificationCategory.SOCIAL,
      },
    ],
    sort: null,
    canMove: true,
    canRemoveFromDisplay: true,
    defaultVisible: true,
  },
  messages: {
    id: "messages",
    label: "Messages",
    description: "Direct and group message notifications.",
    iconKey: "message-circle",
    filters: [
      {
        type: "select",
        name: NOTIFICATION_VIEW_FIELDS.category,
        value: NotificationCategory.MESSAGES,
      },
    ],
    sort: null,
    canMove: true,
    canRemoveFromDisplay: true,
    defaultVisible: true,
  },
} as const satisfies Record<
  NotificationViewBuiltinId,
  {
    canMove: boolean;
    canRemoveFromDisplay: boolean;
    defaultVisible: boolean;
    description: string;
    filters: SerializableNotificationFilter[];
    iconKey: FeedSurfaceIconKey;
    id: NotificationViewBuiltinId;
    label: string;
    sort: SerializableNotificationSort | null;
  }
>;

const BUILTIN_NOTIFICATION_VIEW_ICON_KEYS = new Set<FeedSurfaceIconKey>(
  Object.values(BUILTIN_NOTIFICATION_VIEW_REGISTRY).map((view) => view.iconKey),
);

export const NOTIFICATION_CUSTOM_VIEW_DEFAULT_ICON_KEY: FeedSurfaceIconKey =
  "rocket";

export const NOTIFICATION_CUSTOM_VIEW_ICON_KEYS =
  FEED_MANAGER_ICON_PICKER_KEYS.filter((iconKey) => {
    return !BUILTIN_NOTIFICATION_VIEW_ICON_KEYS.has(iconKey);
  });

export const getNotificationCustomViewIconKey = (
  value?: string | null,
): FeedSurfaceIconKey => {
  const iconKey = getFeedSurfaceIconKey(value);

  return BUILTIN_NOTIFICATION_VIEW_ICON_KEYS.has(iconKey)
    ? NOTIFICATION_CUSTOM_VIEW_DEFAULT_ICON_KEY
    : iconKey;
};

export const BUILTIN_NOTIFICATION_VIEW_ORDER: NotificationViewBuiltinId[] = [
  "all",
  "whales",
  "money",
  "social",
  "messages",
];

export const createBuiltinNotificationViewRef = (
  id: NotificationViewBuiltinId,
): NotificationViewRef => ({
  type: "builtin",
  id,
});

export const getNotificationViewKey = (view: NotificationViewRef) => {
  return `${view.type}:${view.id}`;
};

export const isBuiltinNotificationViewId = (
  value: unknown,
): value is NotificationViewBuiltinId => {
  return (
    typeof value === "string" && value in BUILTIN_NOTIFICATION_VIEW_REGISTRY
  );
};

export const isNotificationViewRef = (
  value: unknown,
): value is NotificationViewRef => {
  if (!isRecord(value)) {
    return false;
  }

  if (value.type === "builtin") {
    return isBuiltinNotificationViewId(value.id);
  }

  return (
    value.type === "custom-notification-view" &&
    typeof value.id === "string" &&
    value.id.length > 0
  );
};

export const areNotificationViewRefsEqual = (
  left?: NotificationViewRef | null,
  right?: NotificationViewRef | null,
) => {
  if (!left || !right) {
    return false;
  }

  return left.type === right.type && left.id === right.id;
};

export const serializeNotificationViewRef = (view: NotificationViewRef) => {
  return JSON.stringify(view);
};

export const parseNotificationViewRef = (
  value: unknown,
): NotificationViewRef | null => {
  if (isNotificationViewRef(value)) {
    return value;
  }

  if (typeof value !== "string" || value.length === 0) {
    return null;
  }

  try {
    const parsed = JSON.parse(value);
    return isNotificationViewRef(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const getDefaultNotificationViewOrder = () => {
  return BUILTIN_NOTIFICATION_VIEW_ORDER.filter(
    (id) => BUILTIN_NOTIFICATION_VIEW_REGISTRY[id].defaultVisible,
  ).map(createBuiltinNotificationViewRef);
};

export const normalizeCustomNotificationViews = (
  value: unknown,
): Record<string, FocusCustomNotificationView> => {
  if (!isRecord(value)) {
    return {};
  }

  return Object.entries(value).reduce<
    Record<string, FocusCustomNotificationView>
  >((views, [entryId, viewValue]) => {
    if (!isRecord(viewValue)) {
      return views;
    }

    const id =
      typeof viewValue.id === "string" && viewValue.id.length > 0
        ? viewValue.id
        : entryId;
    const name =
      typeof viewValue.name === "string" && viewValue.name.trim().length > 0
        ? viewValue.name.trim()
        : null;

    if (!name) {
      return views;
    }

    const createdAt =
      typeof viewValue.createdAt === "string" && viewValue.createdAt.length > 0
        ? viewValue.createdAt
        : new Date(0).toISOString();
    const updatedAt =
      typeof viewValue.updatedAt === "string" && viewValue.updatedAt.length > 0
        ? viewValue.updatedAt
        : createdAt;
    const hiddenNotificationTypesMode =
      normalizeNotificationViewHiddenTypesMode(
        viewValue.hiddenNotificationTypesMode,
      );

    views[id] = {
      createdAt,
      filters: normalizeNotificationFilters(viewValue.filters),
      hiddenNotificationTypes:
        hiddenNotificationTypesMode === "custom"
          ? normalizeHiddenNotificationTypes(viewValue.hiddenNotificationTypes)
          : [],
      hiddenNotificationTypesMode,
      iconKey: getNotificationCustomViewIconKey(
        typeof viewValue.iconKey === "string" ? viewValue.iconKey : null,
      ),
      id,
      name,
      sort: normalizeNotificationSort(viewValue.sort),
      updatedAt,
    };

    return views;
  }, {});
};

export const sanitizeNotificationViewOrder = (
  value: unknown,
  customNotificationViews: Record<string, FocusCustomNotificationView>,
  options?: {
    availableNotificationViewIds?: Iterable<string>;
  },
) => {
  const availableCustomViewIds = new Set(
    options?.availableNotificationViewIds ??
      Object.keys(customNotificationViews),
  );
  const defaults = getDefaultNotificationViewOrder();
  const required = [createBuiltinNotificationViewRef("all")];

  if (!Array.isArray(value)) {
    return defaults;
  }

  const deduped = value.reduce<NotificationViewRef[]>((views, view) => {
    if (!isNotificationViewRef(view)) {
      return views;
    }

    if (
      view.type === "custom-notification-view" &&
      !availableCustomViewIds.has(view.id)
    ) {
      return views;
    }

    const viewKey = getNotificationViewKey(view);

    if (views.some((entry) => getNotificationViewKey(entry) === viewKey)) {
      return views;
    }

    views.push(view);
    return views;
  }, []);

  const requiredKeys = new Set(required.map(getNotificationViewKey));
  const normalized = [
    ...required,
    ...deduped.filter(
      (view) => !requiredKeys.has(getNotificationViewKey(view)),
    ),
  ];

  return normalized.length > 0 ? normalized : defaults;
};

export const resolveNotificationViewItem = (
  view: NotificationViewRef,
  preferences: NotificationViewPreferences,
): NotificationViewResolution | null => {
  if (view.type === "builtin") {
    if (!isBuiltinNotificationViewId(view.id)) {
      return null;
    }

    const builtin = BUILTIN_NOTIFICATION_VIEW_REGISTRY[view.id];

    return {
      filters: builtin.filters,
      hiddenNotificationTypes: [],
      hiddenNotificationTypesMode: "global",
      iconKey: builtin.iconKey,
      key: getNotificationViewKey(view),
      label: builtin.label,
      ref: view,
      sort: builtin.sort,
      type: view.type,
    };
  }

  const customView = preferences.customNotificationViews[view.id];

  if (!customView) {
    return null;
  }

  return {
    customView,
    filters: customView.filters,
    hiddenNotificationTypes: customView.hiddenNotificationTypes,
    hiddenNotificationTypesMode: customView.hiddenNotificationTypesMode,
    iconKey: getFeedSurfaceIconKey(customView.iconKey),
    key: getNotificationViewKey(view),
    label: customView.name,
    ref: view,
    sort: customView.sort,
    type: view.type,
  };
};

export const getNotificationViewHiddenTypes = (
  view: NotificationViewResolution | null | undefined,
  globalHiddenNotificationTypes: NotificationSubCategory[],
) => {
  if (view?.hiddenNotificationTypesMode !== "custom") {
    return globalHiddenNotificationTypes;
  }

  return view.hiddenNotificationTypes;
};

export const resolveNotificationViewItems = (
  preferences: NotificationViewPreferences,
) => {
  return preferences.notificationViewOrder.flatMap((view) => {
    const resolved = resolveNotificationViewItem(view, preferences);
    return resolved ? [resolved] : [];
  });
};

const getFilterArrayValue = (filter: SerializableNotificationFilter) => {
  return Array.isArray(filter.value)
    ? filter.value.filter((entry): entry is string => typeof entry === "string")
    : [];
};

const getFilterStringValue = (filter: SerializableNotificationFilter) => {
  return typeof filter.value === "string" ? filter.value : "";
};

const getNumericBound = (
  filter: SerializableNotificationFilter,
  bound: "max" | "min",
) => {
  if (!isRecord(filter.value)) {
    return undefined;
  }

  const value = filter.value[bound];
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
};

export const notificationMatchesFilters = (
  notification: FocusNotification,
  filters: SerializableNotificationFilter[],
) => {
  const {
    ActorUser,
    Notification: {
      AmountUsdCents,
      Category,
      CreatedAt,
      PostHashHex,
      Status,
      Subcategory,
    },
  } = notification;

  return filters.every((filter) => {
    switch (filter.name) {
      case NOTIFICATION_VIEW_FIELDS.status:
        return getFilterStringValue(filter) === Status;
      case NOTIFICATION_VIEW_FIELDS.category:
        return getFilterStringValue(filter) === Category;
      case NOTIFICATION_VIEW_FIELDS.categories: {
        const categories = getFilterArrayValue(filter);
        return categories.length === 0 || categories.includes(Category);
      }
      case NOTIFICATION_VIEW_FIELDS.excludedCategories:
        return !getFilterArrayValue(filter).includes(Category);
      case NOTIFICATION_VIEW_FIELDS.subcategory:
        return getFilterStringValue(filter) === Subcategory;
      case NOTIFICATION_VIEW_FIELDS.subcategories: {
        const subcategories = getFilterArrayValue(filter);
        return (
          subcategories.length === 0 ||
          subcategories.includes(Subcategory as string)
        );
      }
      case NOTIFICATION_VIEW_FIELDS.excludedSubcategories:
        return !getFilterArrayValue(filter).includes(
          Subcategory as NotificationSubCategory,
        );
      case NOTIFICATION_VIEW_FIELDS.user: {
        const value = getFilterStringValue(filter).toLowerCase();

        if (!value) {
          return true;
        }

        return (
          ActorUser.Username?.toLowerCase().includes(value) ||
          ActorUser.DisplayName?.toLowerCase().includes(value) ||
          ActorUser.PublicKey === value
        );
      }
      case NOTIFICATION_VIEW_FIELDS.post: {
        const value = getFilterStringValue(filter);
        return !value || PostHashHex === value;
      }
      case NOTIFICATION_VIEW_FIELDS.amountUsdCents: {
        const amountUsdCents = AmountUsdCents ?? 0;
        const min = getNumericBound(filter, "min");
        const max = getNumericBound(filter, "max");

        return (
          (typeof min !== "number" || amountUsdCents >= min * 100) &&
          (typeof max !== "number" || amountUsdCents <= max * 100)
        );
      }
      case NOTIFICATION_VIEW_FIELDS.wealthUsdCents: {
        const wealthUsdCents = ActorUser.TotalWealthUsdCents ?? 0;
        const min = getNumericBound(filter, "min");
        const max = getNumericBound(filter, "max");

        return (
          (typeof min !== "number" || wealthUsdCents >= min * 100) &&
          (typeof max !== "number" || wealthUsdCents <= max * 100)
        );
      }
      case NOTIFICATION_VIEW_FIELDS.afterDaysAgo: {
        const days = Number(filter.value);

        if (!Number.isFinite(days) || days <= 0) {
          return true;
        }

        const createdAt = new Date(CreatedAt).getTime();

        if (!Number.isFinite(createdAt)) {
          return true;
        }

        const minCreatedAt = Date.now() - days * 24 * 60 * 60 * 1000;
        return createdAt >= minCreatedAt;
      }
      default:
        return true;
    }
  });
};

export const notificationMatchesUnreadView = (
  notification: FocusNotification,
  view: NotificationViewResolution,
  globalHiddenNotificationTypes: NotificationSubCategory[] = [],
) => {
  const hiddenNotificationTypes = getNotificationViewHiddenTypes(
    view,
    globalHiddenNotificationTypes,
  );

  return (
    notification.Notification.Status === NotificationStatus.PROCESSED &&
    !hiddenNotificationTypes.includes(
      notification.Notification.Subcategory as NotificationSubCategory,
    ) &&
    notificationMatchesFilters(notification, view.filters)
  );
};
